// Internkontroll / compliance / document-intelligence types (split from storage-types.ts).

import type { DataSourceMeta } from './core-types';
import type { DriverCertifications } from './driver-certification-types';

// Base audit metadata for all compliance documents
export interface ComplianceAuditMeta {
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
  updatedBy?: string;
  centralCode: string;
  // Soft delete (for compliance objects that must not be hard-deleted)
  isDeleted?: boolean;
  deletedAt?: string;
  deletedBy?: string;
}

// Linked entity pattern — flat fields for Firestore queryability
export interface LinkedEntities {
  linkedDriverId?: string;
  linkedDriverLabel?: string;
  linkedVehicleId?: string;
  linkedVehicleLabel?: string;
  linkedComplaintId?: string;
  linkedDeviationId?: string;
}

export interface CompliancePermissions {
  viewDeviations: boolean;
  manageDeviations: boolean;
  viewComplaints: boolean;
  manageComplaints: boolean;
  respondToComplaints: boolean;
  viewRiskAssessment: boolean;
  manageRiskAssessment: boolean;
  approveRiskAssessment: boolean;
  viewTraining: boolean;
  manageTraining: boolean;
  viewManagementReview: boolean;
  conductManagementReview: boolean;
  signManagementReview: boolean;
  generateAuditPackage: boolean;
  viewLoyveHMS: boolean;
  manageLoyveHMS: boolean;
  // PR-A2: Pris- og maksimalpris-modul (brevpunkt 11).
  viewPricing: boolean;
  managePricing: boolean;
}

export type LoyveHMSOverallStatus =
  | 'not_applicable'    // No employees, HMS track not required
  | 'unknown'           // Has employees, no data delivered
  | 'incomplete'        // Some delivered, still missing items
  | 'delivered'         // All delivered by løyvehavar, not reviewed by central
  | 'reviewed'          // Reviewed by central, no deficiencies
  | 'deficient'         // Reviewed, deficiencies identified
  | 'follow_up'         // Deficiencies under follow-up
  | 'compliant';        // All in order, reviewed and approved

// PR-A4 — løyvehavar attestations (brevpunkt 6, 10, 12).
//
// Each attestation pairs the løyvehavar's annual self-declaration with the
// sentral's follow-up review. Boolean self-attestation alone does not
// satisfy brevet's requirement that the central must show oppfølging — so
// every attestation has both halves baked into one shape.
//
// Constants below are kept here so the helpers in
// app/lib/internkontroll/owner-attestations.ts and the API routes share a
// single source of truth for the validity window.
export interface SelfAttestationWithCentralReview {
  // ----- Self-declared by the løyvehavar (userId === ownerId) -----
  selfAttested: boolean;
  selfAttestedAt?: string;
  selfAttestedBy?: string;
  // Auto-derived from selfAttestedAt + ATTESTATION_VALIDITY_MONTHS so the
  // dashboard can render it without re-doing the math.
  validUntil?: string;
  // ----- Central follow-up review (manageLoyveHMS) -----
  reviewedByCentral?: boolean;
  reviewedAt?: string;
  reviewedBy?: string;
  reviewNotes?: string;
  // FK list pointing to RegistryDocument ids attached as evidence.
  evidenceDocumentIds?: string[];
}

// 12 months matches the ARSC etterleving-syklus and the staleness window we
// use for the central-review side. Both halves are tested in
// tests/internkontroll/owner-attestations.test.ts.
export const ATTESTATION_VALIDITY_MONTHS = 12;

// How long the central can sit on a self-attestation before it is treated
// as un-reviewed (drives `stale_loyve_hms_attestation`).
export const CENTRAL_REVIEW_STALE_AFTER_DAYS = 365;

// NOTE: 'economic_capacity' was removed — a central cannot assess a
// løyvehavar's economic capacity; that is vetted by Statens vegvesen at
// løyve issuance, never as ongoing internkontroll. Stored
// Owner.economicCapacityAttestation data is left untouched (see
// docs/migrations/remove-economic-capacity-attestation.md).
export type OwnerAttestationKind =
  | 'bokforingsloy'
  | 'central_affiliation';

// Stable keys for the eight HMS checklist points the central reviews
// per løyvehavar. Decoupled from the "*Delivered" boolean field names so
// the review map survives field renames.
export type LoyveHMSItemKey =
  | 'risk_assessment'
  | 'hms_goals'
  | 'routines'
  | 'responsibility_chart'
  | 'training'
  | 'deviation_system'
  | 'annual_review'
  | 'employee_participation';

// Central admin's per-point verdict. 'not_assessed' is the implicit
// default for any point the central has not looked at yet, so an absent
// map entry === not_assessed.
export type CentralReviewItemStatus =
  | 'not_assessed'   // Ikkje vurdert
  | 'ok'             // Ja
  | 'partial'        // Delvis
  | 'missing'        // Nei
  | 'needs_fixing';  // Må utbedrast

export interface CentralReviewItem {
  status: CentralReviewItemStatus;
  note?: string;
  reviewedBy?: string;
  reviewedAt?: string;
}

// Statuses that count as a deficiency the central must follow up.
export const LOYVE_HMS_DEFICIENT_STATUSES: readonly CentralReviewItemStatus[] = [
  'missing',
  'needs_fixing',
];

export interface LoyveHMSStatus extends ComplianceAuditMeta {
  loyveHaverId: string;
  loyveHaverName: string;

  // Basics
  hasEmployees: boolean;
  employeeCount?: number;
  hmsRequired: boolean;  // Auto-set from hasEmployees

  // ── Løyvehavar's HMS status (delivered by løyvehavar) ──
  riskAssessmentDelivered: boolean;
  riskAssessmentDate?: string;
  riskAssessmentDocRef?: string;
  riskAssessmentSource?: DataSourceMeta;

  hmsGoalsDelivered: boolean;
  hmsGoalsDocRef?: string;

  routinesDelivered: boolean;
  routinesDocRef?: string;

  responsibilityChartDelivered: boolean;
  responsibilityChartDocRef?: string;

  trainingDocumented: boolean;
  trainingDocRefs?: string[];

  deviationSystemInPlace: boolean;

  annualReviewCompleted: boolean;
  annualReviewDate?: string;
  annualReviewDocRef?: string;

  employeeParticipation: boolean;
  hasVerneombud?: boolean;

  // Self-attestation
  selfAttested: boolean;
  selfAttestedAt?: string;
  selfAttestedBy?: string;

  // ── Central's follow-up (filled by central) ──
  documentationReceived: boolean;
  documentationReviewedBy?: string;
  documentationReviewedAt?: string;

  deficienciesIdentified: boolean;
  deficiencyDescription?: string;

  // Per-point central review (Ja / Delvis / Nei / Må utbedrast + notat).
  // Absent entry === not_assessed. deficienciesIdentified /
  // deficiencyDescription are derived from this when the central saves.
  centralReviewByItem?: Partial<Record<LoyveHMSItemKey, CentralReviewItem>>;

  followUpDeadline?: string;
  followUpCompleted: boolean;
  followUpCompletedAt?: string;

  linkedDeviationId?: string;

  // Calculated overall status
  overallStatus: LoyveHMSOverallStatus;

  lastUpdatedByLoyve?: string;
  lastUpdatedByLoyveAt?: string;
  lastUpdatedBySentral?: string;
  lastUpdatedBySentralAt?: string;
}

export type DeviationStatus = 'open' | 'in_progress' | 'closed';

export type DeviationCategory =
  | 'safety'
  | 'service_quality'
  | 'vehicle'
  | 'driver_behavior'
  | 'taximeter'
  | 'documentation'
  | 'gdpr'
  | 'working_time'
  | 'hms_deficiency'
  | 'other';

export type DeviationPriority = 'critical' | 'high' | 'medium' | 'low';

export type DeviationSourceType =
  | 'internal'           // Registrert av sentral/admin
  | 'complaint'          // Generert frå klage
  | 'audit'              // Frå tilsyn/revisjon
  | 'whistleblower'      // Anonym varsling (AML kap. 2A)
  | 'system';            // Auto-generert av system

export interface Deviation extends ComplianceAuditMeta, LinkedEntities {
  id: string;
  title: string;
  description: string;
  category: DeviationCategory;
  priority: DeviationPriority;
  status: DeviationStatus;
  reportedAt: string;
  reportedBy: string;
  deadline?: string;
  // Source tracking (v4 dag 2)
  sourceType?: DeviationSourceType;
  isAnonymous?: boolean;          // True for whistleblower reports
  // Corrective action (D14)
  rootCause?: string;
  correctiveAction?: string;
  preventiveAction?: string;
  // Closure
  closedAt?: string;
  closedBy?: string;
  closureNotes?: string;
}

export type ComplaintType =
  | 'price'
  | 'service_quality'
  | 'driver_behavior'
  | 'vehicle_condition'
  | 'discrimination'
  | 'waiting_time'
  | 'other';

export type ComplaintSource = 'web_form' | 'phone' | 'email' | 'in_person';

// PR-A1: How the complaint reached us. Orthogonal to source — a complaint
// can have source='web_form' AND intakeChannel='public_portal' (the public
// /klage form), or source='web_form' AND intakeChannel='internal' (a staff
// member entering it on a customer's behalf via ComplaintManager).
export type ComplaintIntakeChannel = 'internal' | 'public_portal' | 'api' | 'manual';

export const COMPLAINT_PII_RETENTION_POLICY = 'complaint_public_pii_24_months';

export const COMPLAINT_PII_RETENTION_MONTHS = 24;

export type ComplaintStatus =
  | 'new'
  | 'assigned'
  | 'investigating'
  | 'responded'
  | 'escalated_forbrukertilsynet'
  | 'closed';

export type ComplaintResolution =
  | 'pending'
  | 'resolved_in_favor'
  | 'resolved_partial'
  | 'rejected'
  | 'escalated';

export interface InternalNote {
  id: string;
  note: string;
  createdAt: string;
  createdBy: string;
}

export interface Complaint extends ComplianceAuditMeta, LinkedEntities {
  id: string;
  referenceNumber: string;  // Format: "{CENTRALCODE}-{YEAR}-{SEQNR}" e.g. "VT-2026-000042"
  // Classification
  type: ComplaintType;
  source: ComplaintSource;
  // PR-A1: How the complaint was received. Independent from source so we can
  // distinguish "web form via the public /klage portal" from "web form
  // entered internally by a staff member on the customer's behalf".
  intakeChannel?: ComplaintIntakeChannel;
  // PR-A1: ULID-style code shown to the public submitter so they can match
  // their PDF acknowledgement back to a case without exposing the internal id.
  publicReferenceCode?: string;
  // Customer info (NOT visible to driver)
  customerName: string;
  customerEmail?: string;
  customerPhone?: string;
  // Trip details
  tripDate?: string;
  tripDescription?: string;
  driverIdOrVehicle?: string;
  // Content
  description: string;
  customerExpectedResolution?: string;
  // Internal case flow (NOT visible to driver)
  status: ComplaintStatus;
  assignedTo?: string;
  deadline: string;  // Auto-set: createdAt + 14 days. Stored explicitly for query.
  internalNotes: InternalNote[];
  // Response to customer
  responseText?: string;
  respondedAt?: string;
  respondedBy?: string;
  // Result
  resolution: ComplaintResolution;
  // Deviation link
  createsDeviation: boolean;
}

// PR-A1: Customer PII for complaints submitted via the public /klage portal.
// Stored in a side collection so the parent Complaint document can be read by
// staff without leaking customer identity, and so a single TTL field can
// drive both the Firestore TTL policy and the existing GDPR cleanup job.
//
// Path: centrals/{centralCode}/complaints_public_pii/{complaintId}
export interface PublicComplaintPII {
  id: string;             // Same as the parent Complaint document id
  centralCode: string;
  complaintId: string;    // Redundant with id, kept explicit for queries
  customerName: string;
  customerEmail?: string;
  customerPhone?: string;
  // Both Firestore TTL and the existing GDPR ryddejobb read these two
  // fields. Always written together; never trust "we have a cleanup".
  expiresAt: string;      // ISO timestamp, 24 months from createdAt
  retentionPolicy: typeof COMPLAINT_PII_RETENTION_POLICY;
  createdAt: string;
}

// Sanitized summary for driver view (via API route, never direct Firestore)
export interface ComplaintSummary {
  referenceNumber: string;
  type: ComplaintType;
  status: ComplaintStatus;
  resolution: ComplaintResolution;
  tripDate?: string;
  responseText?: string;
  respondedAt?: string;
  createdAt: string;
}

export type RiskLikelihood = 1 | 2 | 3 | 4 | 5;

export type RiskConsequence = 1 | 2 | 3 | 4 | 5;

export type RiskTreatment = 'accepted' | 'mitigating' | 'transferred' | 'avoided';

export interface RiskItem {
  id: string;
  title: string;
  description?: string;
  category: string;
  likelihood: RiskLikelihood;
  consequence: RiskConsequence;
  riskScore: number;  // likelihood × consequence
  treatment: RiskTreatment;
  mitigationPlan?: string;
  responsiblePerson?: string;
  residualLikelihood?: RiskLikelihood;
  residualConsequence?: RiskConsequence;
  residualScore?: number;
}

export interface RiskChangeLogEntry {
  timestamp: string;
  changedBy: string;
  riskItemId: string;
  field: string;
  oldValue: string;   // JSON.stringify for non-string values
  newValue: string;
}

export interface RiskAssessment extends ComplianceAuditMeta {
  id: string;
  title: string;
  period: string;
  status: 'draft' | 'active' | 'archived';
  risks: RiskItem[];
  changeLog: RiskChangeLogEntry[];
  approvedAt?: string;
  approvedBy?: string;
}

export type PricingPolicyStatus = 'draft' | 'confirmed' | 'superseded';

export interface PricingPolicy extends ComplianceAuditMeta {
  id: string;
  status: PricingPolicyStatus;
  // Maksimalpris-felt. Beløp er kr inkl. mva.
  maksimalprisStartTakst: number;
  maksimalprisPerKm: number;
  maksimalprisPerMinutt?: number;
  helgepristilleg?: number;
  nattpristilleg?: number;
  // ISO date (YYYY-MM-DD) — when this policy starts applying.
  effectiveFrom: string;
  // Optional pointer to an uploaded RegistryDocument (vedtak, prisliste, etc.)
  documentRef?: string;
  // Stadfesting / supersedering
  confirmedAt?: string;
  confirmedBy?: string;
  supersededAt?: string;
  supersededByPolicyId?: string;
  notes?: string;
}

export type OrganizationDocumentId =
  | 'responsibility_matrix'
  | 'ownership_conflict_assessment';

export interface ResponsibilityMatrixRole {
  roleLabel: string;            // e.g. "Sentralleiar", "Daglig leiar"
  ownerId?: string;             // optional FK to Owner (admin user)
  ownerName?: string;
  description?: string;
  responsibilities: string[];   // free-text bullet list
}

export interface ResponsibilityMatrix extends ComplianceAuditMeta {
  id: 'responsibility_matrix';
  roles: ResponsibilityMatrixRole[];
  lastReviewedAt?: string;
  lastReviewedBy?: string;
  documentRef?: string;         // optional uploaded organisation chart
  notes?: string;
}

export interface OwnershipConflictAssessment extends ComplianceAuditMeta {
  id: 'ownership_conflict_assessment';
  // True when at least one attached løyvehavar holds an ownership stake in
  // the central. False when the central has assessed and confirmed the
  // absence of such a conflict.
  hasLoyvehaverOwners: boolean;
  assessedAt: string;
  assessedBy: string;
  assessedByName?: string;
  notes?: string;
  // Optional FKs to Owner so the audit pipeline can name affected parties.
  affectedOwnerIds?: string[];
  // Required by brevpunkt 14 when hasLoyvehaverOwners=true: the central
  // must have a documented routine for independent third-party reporting.
  thirdPartyReportingRequired: boolean;
  procedureDocumentId?: string;
}

export interface ExternalAudit extends ComplianceAuditMeta {
  id: string;
  auditorName: string;
  auditorOrgnr?: string;
  auditorEmail?: string;
  auditDate: string;             // ISO date YYYY-MM-DD
  scope?: string;                // free text — "Tilsyn av løyvehavar-HMS", etc.
  findings?: string;             // free text — short summary
  documentRef?: string;          // optional FK to a RegistryDocument
  responseDeadline?: string;     // ISO date — when the central must respond
  responseSubmittedAt?: string;  // ISO date — when the response went out
  notes?: string;
}

export interface OwnerLifecycleArchiveEntry {
  id: string;                 // matches the original document id
  ownerId: string;
  centralCode: string;
  recordType: 'driver_employment' | 'registry_document';
  archivedAt: string;         // when the cloud function copied it
  archivedScanRunId: string;  // links to the lifecycle event below
  // Original payload, stored verbatim so the audit trail is faithful
  // even if the schema changes later. Type opaque on purpose.
  data: Record<string, unknown>;
}

// One per termination — written by the same Cloud Function. Lets the
// audit pipeline list "termineringar i perioden" without scanning the
// full archive sub-tree.
export interface OwnerLifecycleEvent {
  id: string;
  ownerId: string;
  ownerName?: string;
  centralCode: string;
  terminatedAt: string;
  terminatedBy?: string;
  terminationReason?: string;
  archivedEmploymentCount: number;
  archivedDocumentCount: number;
  scanRunId: string;
}

export type MultiCentralMatchKey = 'orgnr' | 'driverId' | 'email';

export interface MultiCentralAlert {
  id: string;
  // The shared key that triggered the match (e.g. orgnr 999111222).
  matchValue: string;
  matchKey: MultiCentralMatchKey;
  // Each conflicting registration. At least two; usually exactly two.
  occurrences: Array<{
    ownerId: string;
    centralCode: string;
    ownerName?: string;
    ownerEmail?: string;
    role?: string;
    approved?: boolean;
  }>;
  // Status: 'open' until a super-admin marks it resolved.
  status: 'open' | 'acknowledged' | 'resolved';
  resolution?: string;        // free text — what the super-admin did
  resolvedAt?: string;
  resolvedBy?: string;
  detectedAt: string;
  // Optional iteration / scan stamp so re-runs of the detector don't
  // create duplicate alerts for the same collision.
  scanRunId?: string;
}

export interface AccessibilityAssessment extends ComplianceAuditMeta {
  id: 'current';
  // Snapshot of the fleet at the time of the assessment.
  totalVehicles: number;
  wheelchairAdaptedVehicles: number;
  // Optional: the central's own coverage target, e.g. 25%.
  targetCoveragePercent?: number;
  // The central's judgement: is the current capacity enough?
  sufficientForDemand: boolean;
  // When sufficientForDemand=false, the gap plan is required by the
  // collector. Free-text — what the central is doing to close the gap.
  planForGap?: string;
  assessedAt: string;
  assessedBy: string;
  assessedByName?: string;
  // ISO date — the central's commitment to revisit the assessment.
  nextReviewBy?: string;
  notes?: string;
}

export interface TrainingCourse extends ComplianceAuditMeta {
  id: string;
  title: string;
  description?: string;
  category: string;
  durationHours?: number;
  isMandatory: boolean;
  validityMonths?: number;  // How long completion is valid
  provider?: string;
}

export interface TrainingRecord extends ComplianceAuditMeta {
  id: string;
  courseId: string;
  courseTitle: string;
  driverId: string;
  driverName: string;
  completedDate: string;
  expiresDate?: string;
  certificateRef?: string;
  notes?: string;
}

export type ManagementReviewSectionStatus = 'not_reviewed' | 'satisfactory' | 'action_required';

export interface ManagementReviewSection {
  key: string;
  title: string;
  status: ManagementReviewSectionStatus;
  autoData?: Record<string, unknown>;  // Auto-populated stats from modules
  comments?: string;
  actionItems?: string;
}

export interface ManagementReview extends ComplianceAuditMeta {
  id: string;
  period: string;
  status: 'draft' | 'in_progress' | 'completed' | 'signed';
  scheduledDate?: string;
  conductedDate: string;
  participants: Array<{ name: string; role: string }>;
  sections: ManagementReviewSection[];
  overallAssessment: string;
  keyDecisions: string;
  // Signing
  signedBy?: string;
  signedAt?: string;
  signatureRole?: string;
  // Locking (v4 M14)
  lockedAt?: string;
  lockedBy?: string;
}

// Signing validation result (v4 M13)
export interface CanSignResult {
  canSign: boolean;
  blockers: string[];
}

export interface AuditSectionResult {
  sectionKey: string;
  title: string;
  hasData: boolean;
  summaryText?: string;
  tableHeaders?: string[];      // Source of truth for column order (v4.1 P7)
  tableRows?: Array<Record<string, string | number>>;
  notes?: string;
}

// Deterministic section ordering (MUST)
export const AUDIT_SECTION_ORDER = [
  'cover_page',
  'table_of_contents',
  'organization_info',
  'regulatory_overview',
  'risk_assessment',
  'deviation_report',
  'complaint_report',
  'competency_overview',
  'loyve_hms_overview',
  'taximeter_status',
  'vehicle_overview',
  'management_review',
  // PR-A2: brevpunkt 11 — maksimalpris-stadfesting per sentral.
  'pricing_policy',
  // PR-A7: brevpunkt 10 aggregat — toppseksjon for obligatorisk
  // opplæring (kontanthandtering + manuell taksameter). Detaljert
  // dekning ligg framleis innbakt i `competency_overview`; denne
  // seksjonen er rein aggregat-overflate som tilsynet kan finne raskt.
  'mandatory_training_status',
  // PR-A7: brevpunkt 6/10/12 aggregat — toppseksjon for løyvehavar-
  // attesteringar. Detaljert dekning ligg framleis innbakt i
  // `loyve_hms_overview`; denne seksjonen er rein aggregat-overflate.
  'attestations_summary',
  // PR-B1: brevpunkt 13 — logg av eksterne tilsyn / revisor-besøk.
  'external_audit_log',
  // PR-C2: lifecycle-logg — når løyvehavarar er termianerte og kva
  // som vart arkivert. Aggregat-overflate så tilsynet ser
  // sentralen sin avviklingsrutine.
  'lifecycle_log',
] as const;

export type AuditSectionKey = typeof AUDIT_SECTION_ORDER[number];

export interface AuditExport extends ComplianceAuditMeta {
  id: string;
  title: string;
  periodFrom: string;
  periodTo: string;
  sections: AuditSectionKey[];
  generatedAt: string;
  generatedBy: string;
  fileUrl?: string;
  fileSize?: number;
}

export type ComplianceNotificationType =
  | 'deviation_critical'
  | 'complaint_new'
  | 'deviation_overdue'
  | 'complaint_overdue'
  | 'certificate_expiring'
  | 'certificate_expired'
  | 'management_review_due'
  | 'risk_assessment_unapproved';

export type NotificationTiming = 'instant' | 'daily_digest' | 'threshold';

export const NOTIFICATION_TIMING: Record<ComplianceNotificationType, NotificationTiming> = {
  deviation_critical: 'instant',
  complaint_new: 'instant',
  deviation_overdue: 'threshold',
  complaint_overdue: 'threshold',
  certificate_expiring: 'daily_digest',
  certificate_expired: 'instant',
  management_review_due: 'daily_digest',
  risk_assessment_unapproved: 'daily_digest',
};

/** Entity type that a document can be linked to */
export type DocumentEntityType = 'driver' | 'loyve';

/** Data type for extraction rules */
export type ExtractionFieldType = 'date' | 'text' | 'number' | 'boolean';

/** Where extracted data should be written */
export type ExtractionTargetRegister =
  | 'driver_certifications'   // DriverCertifications fields
  | 'taximeter_installation'  // TaximeterInstallation fields
  | 'loyve_info'              // Løyve metadata
  | 'vehicle_info'            // CarInfo fields
  | 'none';                   // No auto-populate, display only

/**
 * A single extraction rule — defines one field to extract from a document.
 * Rules are evaluated against the filename and/or document content.
 */
export interface ExtractionRule {
  /** Unique key within the document type, e.g. 'expiryDate', 'serialNumber' */
  fieldKey: string;
  /** Human-readable label shown in UI */
  label: string;
  /** Data type for parsing and validation */
  fieldType: ExtractionFieldType;
  /**
   * Regex pattern to extract this field from filename or content.
   * First capture group is used as the value.
   * Example: '(\d{2}\.\d{2}\.\d{4})' for Norwegian dates
   */
  pattern?: string;
  /** If true, try to extract from filename first */
  extractFromFilename?: boolean;
  /** Target register to auto-populate */
  targetRegister: ExtractionTargetRegister;
  /** Field path in the target register, e.g. 'taxiPermit.expiryDate' */
  targetField?: string;
  /** Whether this field is required for a complete extraction */
  required?: boolean;
}

/**
 * Configurable document type — defines how to classify and parse a category of documents.
 * Users can create new document types with custom extraction rules.
 */
export interface DocumentTypeConfig extends ComplianceAuditMeta {
  id: string;
  /** Display name, e.g. 'Køyreseddel', 'Løyvedokument' */
  name: string;
  /** Short description */
  description?: string;
  /** Which entity type this document belongs to */
  entityType: DocumentEntityType;
  /**
   * Filename patterns to match (case-insensitive).
   * Supports simple substring matching and basic glob.
   * Example: ['køyreseddel', 'kjøreseddel']
   */
  filenamePatterns: string[];
  /** Extraction rules for this document type */
  extractionRules: ExtractionRule[];
  /** Target register for auto-population */
  targetRegister: ExtractionTargetRegister;
  /**
   * For driver_certifications: which certification key to populate.
   * e.g. 'taxiPermit', 'driverLicense', 'firstAid'
   */
  certificationKey?: keyof DriverCertifications;
  /** Whether this is a built-in (non-deletable) type */
  isBuiltIn?: boolean;
  /** Sort order for display */
  sortOrder?: number;
  /** Active flag */
  active: boolean;
}

/**
 * Result of parsing a single field from a document.
 */
export interface ExtractedField {
  fieldKey: string;
  label: string;
  value: string | number | boolean | null;
  rawValue?: string;
  confidence: 'high' | 'medium' | 'low';
  source: 'filename' | 'content' | 'manual';
  targetRegister: ExtractionTargetRegister;
  targetField?: string;
}

/**
 * Full result of document intelligence analysis.
 * Stored alongside the registry document for audit trail.
 */
export interface ParsedDocumentResult {
  id: string;
  /** Reference to the registry document */
  registryDocumentId: string;
  /** Original filename */
  fileName: string;
  /** Matched document type (null if unclassified) */
  documentTypeId: string | null;
  documentTypeName: string | null;
  /** Detected entity */
  entityType: DocumentEntityType | null;
  entityId: string | null;
  entityName: string | null;
  /** Extracted fields */
  extractedFields: ExtractedField[];
  /** Whether extracted data was applied to registers */
  appliedToRegisters: boolean;
  appliedAt?: string;
  appliedBy?: string;
  /** Status of the parsing */
  status: 'parsed' | 'unclassified' | 'error' | 'applied';
  errorMessage?: string;
  /** Audit */
  parsedAt: string;
  parsedBy: string;
  centralCode: string;
}

/**
 * Built-in document type definitions for the taxi industry.
 * These are seeded on first use and cannot be deleted.
 */
export const BUILTIN_DOCUMENT_TYPES: Omit<DocumentTypeConfig, 'id' | 'createdAt' | 'createdBy' | 'updatedAt' | 'updatedBy' | 'centralCode'>[] = [
  {
    name: 'Køyreseddel',
    description: 'Drosjesjåfør-løyve frå politiet',
    entityType: 'driver',
    filenamePatterns: ['køyreseddel', 'kjøreseddel', 'koyreseddel'],
    targetRegister: 'driver_certifications',
    certificationKey: 'taxiPermit',
    extractionRules: [
      { fieldKey: 'expiryDate', label: 'Utløpsdato', fieldType: 'date', pattern: '(\\d{2}[./-]\\d{2}[./-]\\d{4})', extractFromFilename: false, targetRegister: 'driver_certifications', targetField: 'taxiPermit.expiryDate', required: true },
      { fieldKey: 'number', label: 'Nummer', fieldType: 'text', pattern: '(?:nr\\.?|nummer)\\s*:?\\s*(\\S+)', extractFromFilename: false, targetRegister: 'driver_certifications', targetField: 'taxiPermit.number' },
      { fieldKey: 'issuedBy', label: 'Utferda av', fieldType: 'text', targetRegister: 'driver_certifications', targetField: 'taxiPermit.issuedBy' },
    ],
    isBuiltIn: true,
    sortOrder: 1,
    active: true,
  },
  {
    name: 'Førarkort',
    description: 'Førarkort (klasse B/D osv.)',
    entityType: 'driver',
    filenamePatterns: ['førarkort', 'førerkort', 'forerkort'],
    targetRegister: 'driver_certifications',
    certificationKey: 'driverLicense',
    extractionRules: [
      { fieldKey: 'expiryDate', label: 'Utløpsdato', fieldType: 'date', pattern: '(\\d{2}[./-]\\d{2}[./-]\\d{4})', targetRegister: 'driver_certifications', targetField: 'driverLicense.expiryDate', required: true },
      { fieldKey: 'number', label: 'Nummer', fieldType: 'text', targetRegister: 'driver_certifications', targetField: 'driverLicense.number' },
    ],
    isBuiltIn: true,
    sortOrder: 2,
    active: true,
  },
  {
    name: 'Førstehjelpskurs',
    description: 'Dokumentasjon på gjennomført førstehjelpskurs',
    entityType: 'driver',
    filenamePatterns: ['førstehjelp', 'firstaid', 'forstehjelp'],
    targetRegister: 'driver_certifications',
    certificationKey: 'firstAid',
    extractionRules: [
      { fieldKey: 'expiryDate', label: 'Utløpsdato', fieldType: 'date', pattern: '(\\d{2}[./-]\\d{2}[./-]\\d{4})', targetRegister: 'driver_certifications', targetField: 'firstAid.expiryDate' },
      { fieldKey: 'issuedDate', label: 'Dato gjennomført', fieldType: 'date', pattern: '(\\d{2}[./-]\\d{2}[./-]\\d{4})', targetRegister: 'driver_certifications', targetField: 'firstAid.issuedDate' },
    ],
    isBuiltIn: true,
    sortOrder: 3,
    active: true,
  },
  {
    name: 'Glattkøyringkurs',
    description: 'Sertifikat for glattkøyringkurs',
    entityType: 'driver',
    filenamePatterns: ['glattkøyring', 'glattkjøring', 'glattkoyring'],
    targetRegister: 'driver_certifications',
    certificationKey: 'professionalCompetence',
    extractionRules: [
      { fieldKey: 'issuedDate', label: 'Dato gjennomført', fieldType: 'date', pattern: '(\\d{2}[./-]\\d{2}[./-]\\d{4})', targetRegister: 'driver_certifications', targetField: 'professionalCompetence.issuedDate' },
    ],
    isBuiltIn: true,
    sortOrder: 4,
    active: true,
  },
  {
    name: 'ID-kort',
    description: 'Identifikasjonskort for sjåfør',
    entityType: 'driver',
    filenamePatterns: ['id kort', 'id-kort', 'idkort', 'identifikasjon'],
    targetRegister: 'none',
    extractionRules: [],
    isBuiltIn: true,
    sortOrder: 5,
    active: true,
  },
  {
    name: 'Taushetserklæring',
    description: 'Signert taushetserklæring',
    entityType: 'driver',
    filenamePatterns: ['taushetserklæring', 'taushetserklaering', 'tausheit'],
    targetRegister: 'none',
    extractionRules: [
      { fieldKey: 'signedDate', label: 'Signert dato', fieldType: 'date', pattern: '(\\d{2}[./-]\\d{2}[./-]\\d{4})', targetRegister: 'none' },
    ],
    isBuiltIn: true,
    sortOrder: 6,
    active: true,
  },
  {
    name: 'Pasientreiserkurs',
    description: 'Kursbevis for pasientreiser',
    entityType: 'driver',
    filenamePatterns: ['pasientreise', 'pasientreiser'],
    targetRegister: 'driver_certifications',
    extractionRules: [
      { fieldKey: 'issuedDate', label: 'Dato gjennomført', fieldType: 'date', pattern: '(\\d{2}[./-]\\d{2}[./-]\\d{4})', targetRegister: 'driver_certifications' },
      { fieldKey: 'expiryDate', label: 'Utløpsdato', fieldType: 'date', pattern: '(\\d{2}[./-]\\d{2}[./-]\\d{4})', targetRegister: 'driver_certifications' },
    ],
    isBuiltIn: true,
    sortOrder: 7,
    active: true,
  },
  {
    name: 'Løyvedokument',
    description: 'Drosjeløyve utferda av fylkeskommunen',
    entityType: 'loyve',
    filenamePatterns: ['løyvedokument', 'loyvedokument', 'drosjeløyve'],
    targetRegister: 'loyve_info',
    extractionRules: [
      { fieldKey: 'expiryDate', label: 'Utløpsdato', fieldType: 'date', pattern: '(\\d{2}[./-]\\d{2}[./-]\\d{4})', targetRegister: 'loyve_info', targetField: 'loyveExpiryDate' },
      { fieldKey: 'loyveNumber', label: 'Løyvenummer', fieldType: 'text', pattern: '(R-\\d+)', extractFromFilename: true, targetRegister: 'loyve_info', targetField: 'loyveNumber' },
    ],
    isBuiltIn: true,
    sortOrder: 10,
    active: true,
  },
  {
    name: 'Drosjeforsikring',
    description: 'Forsikringsbevis for drosje',
    entityType: 'loyve',
    filenamePatterns: ['drosjeforsikring', 'forsikring'],
    targetRegister: 'loyve_info',
    extractionRules: [
      { fieldKey: 'expiryDate', label: 'Utløpsdato', fieldType: 'date', pattern: '(\\d{2}[./-]\\d{2}[./-]\\d{4})', targetRegister: 'loyve_info', targetField: 'insuranceExpiryDate' },
      { fieldKey: 'insuranceCompany', label: 'Forsikringsselskap', fieldType: 'text', targetRegister: 'loyve_info', targetField: 'insuranceCompany' },
      { fieldKey: 'policyNumber', label: 'Polisenummer', fieldType: 'text', targetRegister: 'loyve_info', targetField: 'insurancePolicyNumber' },
    ],
    isBuiltIn: true,
    sortOrder: 11,
    active: true,
  },
  {
    name: 'Taksametersertifikat',
    description: 'Sertifikat/godkjenning frå Justervesenet',
    entityType: 'loyve',
    filenamePatterns: ['taksametersertifikat', 'taksameter'],
    targetRegister: 'taximeter_installation',
    extractionRules: [
      { fieldKey: 'serialNumber', label: 'Serienummer', fieldType: 'text', pattern: '(?:serie|serial|s\\.?n\\.?)\\s*:?\\s*(\\S+)', targetRegister: 'taximeter_installation', targetField: 'taximeter_serial', required: true },
      { fieldKey: 'brandModel', label: 'Merke/modell', fieldType: 'text', targetRegister: 'taximeter_installation', targetField: 'taximeter_brand_model' },
      { fieldKey: 'controlDate', label: 'Kontrolldato', fieldType: 'date', pattern: '(\\d{2}[./-]\\d{2}[./-]\\d{4})', targetRegister: 'taximeter_installation', targetField: 'install_control_date' },
    ],
    isBuiltIn: true,
    sortOrder: 12,
    active: true,
  },
  {
    name: 'Vognkort',
    description: 'Vognkort (registreringsbevis) for køyretøy',
    entityType: 'loyve',
    filenamePatterns: ['vognkort', 'registreringsbevis'],
    targetRegister: 'vehicle_info',
    extractionRules: [
      { fieldKey: 'regNumber', label: 'Registreringsnummer', fieldType: 'text', pattern: '([A-Z]{2}\\s?\\d{4,5})', targetRegister: 'vehicle_info', targetField: 'regNumber' },
      { fieldKey: 'euControlDate', label: 'Neste EU-kontroll', fieldType: 'date', pattern: '(\\d{2}[./-]\\d{2}[./-]\\d{4})', targetRegister: 'vehicle_info', targetField: 'nextEuControl' },
    ],
    isBuiltIn: true,
    sortOrder: 13,
    active: true,
  },
  {
    name: 'Sentraltilknytning & Løyvegaranti',
    description: 'Bevis på sentraltilknytning og løyvegaranti',
    entityType: 'loyve',
    filenamePatterns: ['sentraltilknytning', 'løyvegaranti', 'loyvegaranti'],
    targetRegister: 'loyve_info',
    extractionRules: [
      { fieldKey: 'validFrom', label: 'Gyldig frå', fieldType: 'date', pattern: '(\\d{2}[./-]\\d{2}[./-]\\d{4})', targetRegister: 'loyve_info' },
    ],
    isBuiltIn: true,
    sortOrder: 14,
    active: true,
  },
  {
    name: 'Dispensasjon frå miljøkrav',
    description: 'Vedtak om dispensasjon frå miljøkrav med vilkår',
    entityType: 'loyve',
    filenamePatterns: ['dispensasjon', 'miljøkrav'],
    targetRegister: 'loyve_info',
    extractionRules: [
      { fieldKey: 'validUntil', label: 'Gyldig til', fieldType: 'date', pattern: '(\\d{2}[./-]\\d{2}[./-]\\d{4})', targetRegister: 'loyve_info' },
      { fieldKey: 'orgNumber', label: 'Org.nr.', fieldType: 'text', pattern: '(?:org\\.?\\s*nr\\.?)\\s*:?\\s*(\\d{9})', extractFromFilename: true, targetRegister: 'loyve_info', targetField: 'orgNumber' },
    ],
    isBuiltIn: true,
    sortOrder: 15,
    active: true,
  },
  // PR-B3 — split sentraltilknytning + løyvegaranti so the audit
  // pipeline can distinguish them. The legacy combined entry above
  // (sortOrder 14) stays put as a fallback for previously parsed
  // documents that still reference its id; new uploads land on these
  // more specific patterns instead.
  {
    name: 'Løyvegaranti',
    description: 'Bevis på løyvegaranti (yrkestransportlov § 4)',
    entityType: 'loyve',
    filenamePatterns: ['løyvegaranti', 'loyvegaranti', 'garanti'],
    targetRegister: 'loyve_info',
    extractionRules: [
      { fieldKey: 'validFrom', label: 'Gyldig frå', fieldType: 'date', pattern: '(?:gyldig\\s*frå|fra)\\s*:?\\s*(\\d{2}[./-]\\d{2}[./-]\\d{4})', targetRegister: 'loyve_info' },
      { fieldKey: 'validUntil', label: 'Gyldig til', fieldType: 'date', pattern: '(?:gyldig\\s*til|til)\\s*:?\\s*(\\d{2}[./-]\\d{2}[./-]\\d{4})', targetRegister: 'loyve_info' },
    ],
    isBuiltIn: true,
    sortOrder: 16,
    active: true,
  },
  {
    name: 'Sentraltilknytning',
    description: 'Bevis på sentraltilknytning frå drosjesentral',
    entityType: 'loyve',
    filenamePatterns: ['sentraltilknytning', 'sentraltilknyt', 'tilknytting'],
    targetRegister: 'loyve_info',
    extractionRules: [
      { fieldKey: 'validFrom', label: 'Gyldig frå', fieldType: 'date', pattern: '(?:gyldig\\s*frå|fra)\\s*:?\\s*(\\d{2}[./-]\\d{2}[./-]\\d{4})', targetRegister: 'loyve_info' },
    ],
    isBuiltIn: true,
    sortOrder: 17,
    active: true,
  },
  // PR-B3 — løyvehavar attestation evidence (PR-A4). The actual
  // attestation lives on the Owner doc; uploaded documents are
  // proof / vedlegg. Both are filed against the loyve entity rather
  // than the driver since attestations are per-løyve responsibilities.
  {
    name: 'Attestering — bokføringslova',
    description: 'Vedlegg/dokumentasjon for sjølv-attestering av bokføringslov-etterleving (PR-A4)',
    entityType: 'loyve',
    filenamePatterns: ['bokføringslov', 'bokforingslov', 'bokforing', 'bokføring'],
    targetRegister: 'loyve_info',
    extractionRules: [
      { fieldKey: 'attestedAt', label: 'Attestert dato', fieldType: 'date', pattern: '(\\d{2}[./-]\\d{2}[./-]\\d{4})', targetRegister: 'loyve_info' },
    ],
    isBuiltIn: true,
    sortOrder: 19,
    active: true,
  },
];
